import { ReactNode, useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import api from "../../../../hooks/api-new.ts";
import classNames from "./Settings.module.css";
import { useAxios } from "../../../../hooks/axios.ts";
import Loader from "../../../common/Loader.tsx";
import LanguageMultiSelect from "../../../common/LanguageMultiSelect.tsx";
import DefaultVoiceAvatarSetting from "./DefaultVoiceAvatarSetting.tsx";
import GlossarySetting from "./GlossarySetting.tsx";
import ManageMembers from "./ManageMembers.tsx";
import { useOrganization } from "../../../context/organization/organization.ts";
import TextCustomization from "../../../common/settings/TextCustomization.tsx";
import TextSpeed from "../../../common/settings/TextSpeed.tsx";
import { useDefaultSettings } from "../../../../hooks/settings.ts";
import Toggle from "../../../common/Toggle.tsx";

export function Setting(props: {
  title: string;
  subtitle?: string;
  className?: string;
  children: ReactNode | ReactNode[];
}) {
  return (
    <div className={classNames.Setting}>
      <div className={classNames.Titles}>
        <h1>{props.title}</h1>
        {props.subtitle && <h2>{props.subtitle}</h2>}
      </div>
      <div className={props.className}>{props.children}</div>
    </div>
  );
}

export default function Settings() {
  useDefaultSettings();
  const { organization, setOrganization, loading } = useOrganization();

  const axios = useAxios();
  const updateOrganization = api.useUpdateOrganization(axios);

  useEffect(() => {
    if (!organization) {
      return;
    }
    const debounce = setTimeout(async () => {
      await updateOrganization(organization);
    }, 500);
    return () => {
      clearTimeout(debounce);
    };
  }, [organization, updateOrganization]);

  if (!organization || loading) {
    return <Loader />;
  }

  return (
    <div className={classNames.Settings}>
      <Routes>
        <Route
          path="/"
          element={
            <>
              <Setting title="Admin management">
                <ManageMembers />
              </Setting>
            </>
          }
        />

        <Route
          path="/languages"
          element={
            <Setting
              title="Languages"
              subtitle={`Please select your languages (minimum of 2 required). Your organization can select up to ${organization.max_languages_allowed} languages`}
            >
              <div className={classNames.LanguageSetting}>
                <LanguageMultiSelect
                  selectedLanguages={organization.default_languages_setting}
                  onChange={(languages) => {
                    setOrganization((prevState) => ({
                      ...prevState!,
                      default_languages_setting: languages,
                    }));
                  }}
                  maxLanguages={organization.max_languages_allowed}
                />
              </div>
            </Setting>
          }
        />

        {organization.glossary_enabled && (
          <Route path="/glossary" element={<GlossarySetting />} />
        )}

        {organization.voice_translation_enabled && (
          <Route
            path="/voice-translation"
            element={
              <Setting
                title="Voice translation"
                subtitle="Enable voice-to-voice translation by default"
              >
                <div className={classNames.ToggleSetting}>
                  <label>
                    Turn on voice-to-voice translation for all participants by
                    default
                  </label>
                  <Toggle
                    enabled={organization.default_ai_voice_setting}
                    onChange={() => {
                      setOrganization((prevState) => ({
                        ...prevState!,
                        default_ai_voice_setting:
                          !prevState?.default_ai_voice_setting,
                      }));
                    }}
                  />
                </div>
              </Setting>
            }
          />
        )}

        {organization.voice_translation_enabled && (
          <Route
            path="/voice-avatars"
            element={
              <DefaultVoiceAvatarSetting
                organization={organization}
                onChange={(organization) => setOrganization(organization)}
              />
            }
          />
        )}

        {organization.recording_enabled && (
          <Route
            path="/recording"
            element={
              <Setting title="Recording" subtitle="Enable recording by default">
                <div className={classNames.ToggleSetting}>
                  <label>
                    Turn on recording for all participants by default
                  </label>
                  <Toggle
                    enabled={organization.default_recording_setting}
                    onChange={() => {
                      setOrganization((prevState) => ({
                        ...prevState!,
                        default_recording_setting:
                          !prevState?.default_recording_setting,
                      }));
                    }}
                  />
                </div>
              </Setting>
            }
          />
        )}

        <Route
          path="/qna"
          element={
            <Setting title="Q&A" subtitle="Enable multilingual Q&A by default">
              <div className={classNames.ToggleSetting}>
                <label>
                  Turn on multilingual Q&A for all participants by default{" "}
                </label>
                <Toggle
                  enabled={organization.default_qna_setting}
                  onChange={() => {
                    setOrganization((prevState) => ({
                      ...prevState!,
                      default_qna_setting: !prevState?.default_qna_setting,
                    }));
                  }}
                />
              </div>
            </Setting>
          }
        />

        <Route
          path="/text-translation"
          element={
            <Setting
              title="Text translation"
              subtitle="Please customize your text translation"
            >
              <div className={classNames.TextTranslationSetting}>
                <TextCustomization />
                <TextSpeed />
              </div>
            </Setting>
          }
        />
      </Routes>
    </div>
  );
}
